import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Analytics
 * ---------
 * Loads Google Analytics 4 (gtag.js) once on app mount and fires a `page_view`
 * event on every React Router route change. SPAs need this because gtag's
 * default page_view only fires on the initial HTML load.
 *
 * Config via env (runtime, no rebuild for changes in dev):
 *   REACT_APP_GA4_MEASUREMENT_ID = "G-XXXXXXXXXX"
 *
 * When the env var is unset (dev, preview), this component is a total no-op.
 * Safe to render unconditionally — zero network calls without an ID.
 *
 * Admin/internal paths are excluded so Derwin's own clicks don't pollute the
 * funnel metrics.
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

const EXCLUDED_PREFIXES = ['/admin', '/app', '/profile', '/payment-success', '/subscription-success'];

// Respect the cookie-consent choice. Imported lazily to avoid a circular dep
// risk; if the module isn't loaded yet (first paint) we default to tracking
// since the banner suppresses GA until user answers.
const CONSENT_STORAGE_KEY = 'tp_cookie_consent';
const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const { value, expires } = JSON.parse(raw);
    if (Date.now() > expires) return null;
    return value;  // 'granted' | 'denied'
  } catch {
    return null;
  }
};

const shouldTrack = (pathname) => {
  if (!GA_ID) return false;
  if (getConsent() === 'denied') return false;
  return !EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

let gaBootstrapped = false;

const bootstrapGA = () => {
  if (gaBootstrapped || !GA_ID || typeof window === 'undefined') return;
  gaBootstrapped = true;

  // Load gtag.js async
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Init dataLayer + gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag('js', new Date());
  // send_page_view:false — we fire page_view manually on route change below so
  // SPA navigation is counted correctly. Anonymize IP for light privacy posture.
  gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
};

export const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Only bootstrap once user consents (or if no tracking IS configured,
    // shouldTrack will short-circuit harmlessly anyway)
    if (getConsent() === 'granted') bootstrapGA();

    // React to the banner's choice without needing a page reload
    const onConsentChange = (e) => {
      if (e.detail === 'granted') bootstrapGA();
    };
    window.addEventListener('tp:consent-changed', onConsentChange);
    return () => window.removeEventListener('tp:consent-changed', onConsentChange);
  }, []);

  useEffect(() => {
    if (!shouldTrack(location.pathname)) return;
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
};

/**
 * trackEvent(name, params) — call from anywhere to log a custom event.
 * Example: trackEvent('water_budget_calculated', { zip: '78664' })
 * Silently no-ops when GA is not configured.
 */
export const trackEvent = (name, params = {}) => {
  if (!GA_ID || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', name, params);
  } catch (_) { /* swallow */ }
};

export default Analytics;
